/* Copyright (c) 2020-present CloudIO Inc */

import { IoImages, Query } from '@cloudio-saas/datasource-types';
import { useStateXSelector } from '@cloudio-saas/statex';
import { FormControl, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ImgAttributeChooserProp } from 'componentregistry';
import { VirtualizedSelectInput } from 'custom/SelectInput';
import { helpTextSelector } from 'designer/state';
import React, { useEffect } from 'react';
import useAppStore from 'store/useAppStore';
import { getIoImagesStoreProps } from 'store/utils/utils';
import WaveDots from 'utils/WaveDots';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

const imageFilter: (appUid: string) => Query<IoImages> = (appUid: string) => ({
  filter: [{ appUid: { is: appUid } }, { imageUid_activeVersion: { is: 'Y' } }],
  limit: 5000,
  offset: 0,
  projection: {
    imageUid: 1,
    imageUid_fileName: 1,
    imageUid_fileSize: 1,
    imageUid_mimeType: 1,
    orgUid: 1,
    lastUpdateDate: 1,
    lastUpdatedBy: 1,
  },
  sort: { imageUid_fileName: 1 },
});

export function ImgChooser({
  appUid,
  helpText,
  label,
  onChange,
  required,
  testid,
  value,
  isViewOnlyUser = false,
}: {
  appUid: string;
  helpText?: string;
  label: string;
  onChange: (val: string) => void;
  required?: boolean;
  testid: string;
  value: string;
  isViewOnlyUser?: boolean;
}) {
  const { query, records, isBusy } = useAppStore<IoImages>(
    getIoImagesStoreProps(appUid),
  );

  useEffect(() => {
    query({ props: imageFilter(appUid) });
  }, [appUid, query]);

  const classes = useStyles();
  const options = Object.values(records).map((row) => ({
    value: row.imageUid ?? 'Missing imageUid!',
    label: row.imageUid_fileName ?? row.imageUid,
  }));

  return (
    <FormControl
      disabled={isViewOnlyUser}
      data-testid={testid}
      required={required}
      fullWidth
      variant="standard"
      component="fieldset"
      className={classes.root}
    >
      <VirtualizedSelectInput
        value={value}
        options={options}
        onChange={onChange}
        label={label || ''}
        startIcon={isBusy ? <WaveDots /> : null}
        disabled={isBusy || isViewOnlyUser}
        placeholder={isBusy ? 'Loading ...' : 'Choose an image'}
        variant="standard"
        required={required}
      />
      <FormHelperText>{helpText}</FormHelperText>
    </FormControl>
  );
}

export function ImgAttributeChooserProperty({
  appUid,
  activeItemId,
  hmr,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<ImgAttributeChooserProp, string>) {
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange = (image: string) => {
    if (image !== value) {
      onChange(property, image);
    }
  };

  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <ImgChooser
      isViewOnlyUser={isViewOnlyUser}
      appUid={appUid}
      testid={property}
      value={value}
      onChange={handleChange}
      label={def.label}
      required={required}
      helpText={helpText}
    />
  );
}
